import { createFileRoute, Link } from "@tanstack/react-router";
import { PageHero } from "@/components/site/Layout";
import { api } from "@/lib/api";
import { useState, useEffect } from "react";
import { ArrowLeft } from "lucide-react";

interface Sponsor {
  id: number;
  name: string;
  image_url: string | null;
  description: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export const Route = createFileRoute("/sponsors/$sponsorId")({
  component: SponsorDetail,
});

function SponsorDetail() {
  const { sponsorId } = Route.useParams() as { sponsorId: string };
  const [sponsor, setSponsor] = useState<Sponsor | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSponsor();
  }, [sponsorId]);

  const loadSponsor = async () => {
    try {
      const response = await api.get<Sponsor>(`/api/sponsors/${sponsorId}`);
      if (response.success && response.data) {
        setSponsor(response.data);
      } else {
        setError("Sponsor not found");
      }
    } catch (err) {
      console.error("Failed to load sponsor:", err);
      setError("Failed to load sponsor details");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <PageHero
          eyebrow="Sponsor Profile"
          title="Loading..."
          subtitle=""
        />
        <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto text-center text-muted-foreground">Loading sponsor details...</div>
        </section>
      </>
    );
  }

  if (error || !sponsor) {
    return (
      <>
        <PageHero
          eyebrow="Sponsor Profile"
          title="Sponsor Not Found"
          subtitle=""
        />
        <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto text-center">
            <p className="text-muted-foreground mb-6">{error || "Sponsor not found"}</p>
            <Link to="/sponsors" className="inline-flex items-center text-primary hover:text-accent transition-colors">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Sponsors
            </Link>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <section className="relative">
        {/* Hero Section with Background */}
        <div className="relative bg-gradient-to-br from-primary via-primary/90 to-primary/80 overflow-hidden">
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '32px 32px'
          }}></div>
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 md:py-24">
            <Link 
              to="/sponsors" 
              className="inline-flex items-center text-white/80 hover:text-white transition-colors mb-8 group"
            >
              <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
              Back to Sponsors
            </Link>
            
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Logo */}
              <div className="flex justify-center lg:justify-start">
                <div className="relative">
                  <div className="absolute -inset-4 bg-white/10 rounded-3xl blur-xl"></div>
                  <div className="relative bg-white rounded-2xl p-8 sm:p-10 md:p-12 shadow-2xl max-w-sm w-full">
                    {sponsor.image_url ? (
                      <img
                        src={sponsor.image_url}
                        alt={sponsor.name}
                        className="w-full h-auto object-contain"
                      />
                    ) : (
                      <div className="font-serif text-3xl md:text-4xl text-primary text-center leading-tight">
                        {sponsor.name}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Info */}
              <div className="text-white text-center lg:text-left">
                <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl font-bold mb-4 leading-tight">
                  {sponsor.name}
                </h1>
              </div>
            </div>
          </div>
        </div>
        
        {/* Content Section */}
        <section className="py-16 sm:py-20 md:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-background to-background/95">
          <div className="max-w-4xl mx-auto">
            {sponsor.description && (
              <div className="bg-white rounded-2xl shadow-lg p-8 sm:p-10 md:p-12 border border-border/50">
                <h2 className="font-serif text-2xl sm:text-3xl text-primary mb-6">About</h2>
                <div
                  className="text-muted-foreground leading-relaxed prose prose-base max-w-none"
                  dangerouslySetInnerHTML={{ __html: sponsor.description }}
                />
              </div>
            )}
          </div>
        </section>
      </section>
    </>
  );
}
